import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  DiaryDay,
  MealItem,
  MealType,
  addItem,
  deleteItem,
  getDay,
  updateItem,
} from "../api/diary";
import { Product, listProducts } from "../api/products";
import { ThemeParams } from "../telegram";
import { servingLabel, unitInputLabel, unitLabel } from "../utils/units";
import DiaryHeader from "../components/DiaryHeader";
import DaySummary from "../components/DaySummary";
import MealCard from "../components/MealCard";

export type DiaryProps = {
  theme: ThemeParams;
  scheme: "light" | "dark";
  userId: number;
  topOffset?: number;
  leftOffset?: number;
  rightOffset?: number;
  isFullscreen?: boolean;
};

const MEAL_ORDER: MealType[] = ["breakfast", "lunch", "dinner", "snack"];

const MEAL_TITLES: Record<MealType, string> = {
  breakfast: "Завтрак",
  lunch: "Обед",
  dinner: "Ужин",
  snack: "Перекус/Другое",
};

const WEEKDAYS_SHORT = ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"];
const MONTHS_SHORT = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

function toIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayIso(): string {
  return toIso(new Date());
}

function shiftDate(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toIso(dt);
}

function humanDate(iso: string): string {
  const today = todayIso();
  const yesterday = shiftDate(today, -1);
  if (iso === today) return "Сегодня";
  if (iso === yesterday) return "Вчера";
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${WEEKDAYS_SHORT[dt.getDay()]}, ${dt.getDate()} ${MONTHS_SHORT[dt.getMonth()]}`;
}

const KCAL_GOAL = 2000;

export default function Diary({ theme, scheme, userId, topOffset = 0, leftOffset = 0, rightOffset = 0, isFullscreen = false }: DiaryProps) {
  const [date, setDate] = useState<string>(todayIso());
  const [day, setDay] = useState<DiaryDay | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [addModal, setAddModal] = useState<{ meal: MealType } | null>(null);
  const [editItem, setEditItem] = useState<MealItem | null>(null);

  const bg = theme.bg_color ?? (scheme === "dark" ? "#000000" : "#ffffff");
  const fg = theme.text_color ?? (scheme === "dark" ? "#f5f5f5" : "#1a1a1a");
  const hint = theme.hint_color ?? (scheme === "dark" ? "#8E8E93" : "#999999");
  const btnBg = theme.button_color ?? "#3390ec";
  const cardBg = scheme === "dark" ? "rgba(255,255,255,0.04)" : "#f4f4f5";
  const divider = scheme === "dark" ? "rgba(255,255,255,0.06)" : "#e6e8eb";
  const accent = "#66BB6A";
  const addBtn = "#43A047";

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await getDay(date, userId);
      setDay(data);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [date, userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  async function onAdded() {
    setAddModal(null);
    await reload();
  }

  async function onDeleted() {
    setEditItem(null);
    await reload();
  }

  const cardTheme = { fg, hint, cardBg, divider, accent, addBtn };

  return (
    <div style={{ minHeight: "100vh", width: "100%", maxWidth: "100%", overflowX: "hidden", background: bg, color: fg, boxSizing: "border-box", fontFamily: "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif" }}>
      <DiaryHeader
        date={date}
        onChange={setDate}
        topOffset={topOffset}
        leftOffset={leftOffset}
        rightOffset={rightOffset}
        isFullscreen={isFullscreen}
      />

      <div style={{ padding: "12px 8px 8px 8px", fontSize: 22, fontWeight: 700, color: fg, lineHeight: 1.1, boxSizing: "border-box" }}>
        {humanDate(date)}
      </div>

      <DaySummary totals={day?.totals} cardBg={cardBg} hint={hint} fg={fg} kcalGoal={KCAL_GOAL} />

      {err && <div style={{ color: "#e54848", margin: "8px 8px 0", fontSize: 12 }}>{err}</div>}

      {MEAL_ORDER.map((meal) => {
        const m = day?.meals[meal];
        return (
          <MealCard
            key={meal}
            meal={meal}
            title={MEAL_TITLES[meal]}
            totals={m?.totals ?? { kcal: 0, protein: 0, fat: 0, carbs: 0 }}
            items={m?.items ?? []}
            onAdd={() => setAddModal({ meal })}
            onItemTap={(it) => setEditItem(it)}
            theme={cardTheme}
            kcalGoal={KCAL_GOAL}
          />
        );
      })}

      {loading && !day && (
        <div style={{ color: hint, fontSize: 13, textAlign: "center", marginTop: 16 }}>загрузка…</div>
      )}

      {addModal && (
        <AddItemModal
          meal={addModal.meal}
          date={date}
          userId={userId}
          onClose={() => setAddModal(null)}
          onSaved={onAdded}
          theme={{ bg, fg, hint, btnBg, cardBg, divider, accent }}
        />
      )}

      {editItem && (
        <EditItemModal
          item={editItem}
          onClose={() => setEditItem(null)}
          onSaved={onDeleted}
          theme={{ bg, fg, hint, btnBg, cardBg }}
        />
      )}
    </div>
  );
}

// ───────── Add modal

type AddModalProps = {
  meal: MealType;
  date: string;
  userId: number;
  onClose: () => void;
  onSaved: () => void;
  theme: { bg: string; fg: string; hint: string; btnBg: string; cardBg: string; divider: string; accent: string };
};

function AddItemModal({ meal, date, userId, onClose, onSaved, theme }: AddModalProps) {
  const [step, setStep] = useState<"search" | "amount">("search");
  const [q, setQ] = useState("");
  const [debQ, setDebQ] = useState("");
  const [results, setResults] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [product, setProduct] = useState<Product | null>(null);
  const [amountStr, setAmountStr] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (step !== "search") return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listProducts(debQ || undefined)
      .then((r) => { if (!cancelled) setResults(r); })
      .catch((e) => { if (!cancelled) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [debQ, step]);

  function pick(p: Product) {
    setProduct(p);
    setAmountStr(p.unit === "piece" ? "1" : "100");
    setStep("amount");
  }

  async function submit() {
    if (!product) return;
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErr("Введите корректное количество");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await addItem({
        date,
        meal_type: meal,
        product_id: product.id,
        amount,
        user_id: userId,
      });
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: theme.bg,
    color: theme.fg,
    border: `1px solid ${theme.divider}`,
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 16,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };

  const mealTitleShort: Record<MealType, string> = {
    breakfast: "завтрак",
    lunch: "обед",
    dinner: "ужин",
    snack: "перекус",
  };

  return (
    <ModalShell onClose={onClose} theme={theme}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        {step === "amount" && (
          <button onClick={() => setStep("search")} style={{ background: "transparent", color: theme.btnBg, border: "none", padding: 0, fontSize: 15, cursor: "pointer" }}>
            ‹
          </button>
        )}
        <div style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>
          {step === "search" ? `Добавить в ${mealTitleShort[meal]}` : product?.name}
        </div>
        <button onClick={onClose} style={{ background: "transparent", color: theme.hint, border: "none", padding: 0, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>

      {step === "search" && (
        <>
          <input
            style={inputStyle}
            placeholder="Поиск по названию или бренду…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
          />
          {err && <div style={{ color: "#e54848", marginTop: 10, fontSize: 13 }}>{err}</div>}
          <div style={{ marginTop: 12, maxHeight: "55vh", overflowY: "auto" }}>
            {loading && <div style={{ color: theme.hint, fontSize: 13, padding: 8 }}>загрузка…</div>}
            {!loading && results.length === 0 && (
              <div style={{ color: theme.hint, fontSize: 13, padding: 8 }}>{debQ ? "ничего не найдено" : "пусто"}</div>
            )}
            {results.map((p, idx) => {
              const s = servingLabel(p.unit);
              return (
                <button
                  key={p.id}
                  onClick={() => pick(p)}
                  style={{
                    display: "block",
                    width: "100%",
                    background: "transparent",
                    border: "none",
                    borderTop: idx === 0 ? "none" : `1px solid ${theme.divider}`,
                    padding: "10px 4px",
                    textAlign: "left",
                    cursor: "pointer",
                    color: theme.fg,
                    fontFamily: "inherit",
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {p.name}
                    {p.brand && <span style={{ fontWeight: 400, color: theme.hint }}>{` (${p.brand})`}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: theme.accent, marginTop: 2 }}>
                    {s.amount} {s.label} · {Number(p.kcal_100g).toFixed(0)} ккал
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {step === "amount" && product && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ fontSize: 13, color: theme.hint }}>
            На 100 {unitLabel(product.unit) === "шт" ? "шт" : unitLabel(product.unit)}: {Number(product.kcal_100g).toFixed(0)} ккал ·
            Б {Number(product.protein_100g).toFixed(1)} ·
            Ж {Number(product.fat_100g).toFixed(1)} ·
            У {Number(product.carbs_100g).toFixed(1)}
          </div>
          <div>
            <label style={{ fontSize: 13, color: theme.hint, marginBottom: 4, display: "block" }}>{unitInputLabel(product.unit)}</label>
            <input
              style={inputStyle}
              type="number"
              inputMode="decimal"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              autoFocus
            />
          </div>
          {err && <div style={{ color: "#e54848", fontSize: 13 }}>{err}</div>}
          <PreviewMacros product={product} amount={Number(amountStr) || 0} theme={theme} />
          <button
            onClick={submit}
            disabled={submitting}
            style={{ background: theme.btnBg, color: "#fff", border: "none", borderRadius: 10, padding: "12px 18px", fontSize: 15, fontWeight: 500, cursor: submitting ? "wait" : "pointer", width: "100%" }}
          >
            {submitting ? "..." : "Добавить"}
          </button>
        </div>
      )}
    </ModalShell>
  );
}

function PreviewMacros({ product, amount, theme }: { product: Product; amount: number; theme: { fg: string; hint: string; cardBg: string } }) {
  const factor = amount / 100;
  const kcal = Number(product.kcal_100g) * factor;
  const p = Number(product.protein_100g) * factor;
  const f = Number(product.fat_100g) * factor;
  const c = Number(product.carbs_100g) * factor;
  return (
    <div style={{ background: theme.cardBg, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: theme.fg, display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span>{Math.round(kcal)} ккал</span>
      <span style={{ color: theme.hint }}>Б {p.toFixed(1)} · Ж {f.toFixed(1)} · У {c.toFixed(1)}</span>
    </div>
  );
}

// ───────── Edit modal (change amount / delete)

type EditModalProps = {
  item: MealItem;
  onClose: () => void;
  onSaved: () => void;
  theme: { bg: string; fg: string; hint: string; btnBg: string; cardBg: string };
};

function EditItemModal({ item, onClose, onSaved, theme }: EditModalProps) {
  const [amountStr, setAmountStr] = useState(String(Number(item.amount)));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save() {
    const amount = Number(amountStr);
    if (!Number.isFinite(amount) || amount <= 0) {
      setErr("Введите корректное количество");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      await updateItem(item.id, amount);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  async function remove() {
    if (!confirm(`Удалить «${item.product.name}»?`)) return;
    setSubmitting(true);
    setErr(null);
    try {
      await deleteItem(item.id);
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    background: theme.bg,
    color: theme.fg,
    border: `1px solid ${theme.hint}40`,
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 16,
    width: "100%",
    boxSizing: "border-box",
    outline: "none",
  };

  return (
    <ModalShell onClose={onClose} theme={{ bg: theme.bg, cardBg: theme.cardBg }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>{item.product.name}</div>
        <button onClick={onClose} style={{ background: "transparent", color: theme.hint, border: "none", padding: 0, fontSize: 18, cursor: "pointer", lineHeight: 1 }}>×</button>
      </div>
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <label style={{ fontSize: 13, color: theme.hint, marginBottom: 4, display: "block" }}>{unitInputLabel(item.product.unit)}</label>
          <input style={inputStyle} type="number" inputMode="decimal" value={amountStr} onChange={(e) => setAmountStr(e.target.value)} autoFocus />
        </div>
        {err && <div style={{ color: "#e54848", fontSize: 13 }}>{err}</div>}
        <button
          onClick={save}
          disabled={submitting}
          style={{ background: theme.btnBg, color: "#fff", border: "none", borderRadius: 10, padding: "12px 18px", fontSize: 15, fontWeight: 500, cursor: submitting ? "wait" : "pointer", width: "100%" }}
        >
          {submitting ? "..." : "Сохранить"}
        </button>
        <button
          onClick={remove}
          disabled={submitting}
          style={{ background: "transparent", color: "#e54848", border: "none", padding: "8px 18px", fontSize: 14, cursor: "pointer", width: "100%" }}
        >
          Удалить
        </button>
      </div>
    </ModalShell>
  );
}

// ───────── Modal shell

function ModalShell({ onClose, theme, children }: { onClose: () => void; theme: { bg: string; cardBg: string }; children: ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 100,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.bg,
          width: "100%",
          maxWidth: 520,
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          padding: "16px 20px calc(20px + max(env(safe-area-inset-bottom), var(--tg-safe-area-inset-bottom, 0px)))",
          maxHeight: "85vh",
          overflowY: "auto",
          boxSizing: "border-box",
        }}
      >
        {children}
      </div>
    </div>
  );
}
